import { CheckboxYesNo, FormContainer, LabeledInput, Worksheet } from "uikit"
import React from "react"
import { Dropdown, Grid, Select } from "semantic-ui-react"
import { useStore } from "store"
import { Footer } from "./Footer"
import { formatCurrency } from "uikit/utilities"

const REPAYMENT_PLAN_OPTIONS = [
  { text: "Standard/Extended", value: "standard" },
  { text: "REPAYE", value: "repaye" },
  { text: "PAYE", value: "paye" },
  { text: "IBR", value: "ibr" },
  { text: "ICR", value: "icr" }
]

const REPAYMENT_PLAN_DESCRIPTIONS = {
  standard: (
    <>
      Standard repayment is the most popular repayment plan and default option
      for borrowers who have not chosen a repayment plan. It is a level payment
      plan with up to 120 fixed monthly payments during a repayment term of up
      to 10 years. Extended Repayment Plans may be fixed or graduated and will
      ensure that your loans get paid off within 25 years. SAVVI only considers
      the fixed version of those plans, which offer a level payment plan for
      some type of federal student and parent loans. One version is available
      only for federal consolidation loans and the other for new borrowers after
      October 7, 1998 who have more than $30,000 in federal student loan debt.
    </>
  ),
  repaye: (
    <>
      The Revised Pay as You Earn (REPAYE) repayment plan is an income-dependent
      repayment plan available to Direct Loan borrowers with eligible loans
      (which do not include Uninsured private loans, Parent Plus loans, Perkins
      loans, FFEL program loans, nor consolidation loans that repay those). The
      monthly minimum payments for the are 10% of discretionary income. Your
      remaining balance may be subject to forgiveness after a certain number of
      years.
    </>
  ),
  paye: (
    <>
      The Pay as You Earn (PAYE) repayment plan is an income-dependent repayment
      plan available to Direct Loan borrowers with eligible loans (which do not
      include Uninsured private loans, Parent Plus loans, Perkins loans, FFEL
      program loans nor consolidation loans that repay those). The monthly
      minimum payments are the lesser of 10% of discretionary income and the
      standard 10-year paydown schedule. Your remaining balance may be subject
      to forgiveness after a certain number of years. You must have high debt
      relative to your income to be eligible for this repayment plan and a new
      borrower after October 1, 2007.
    </>
  ),
  ibr: (
    <>
      Income-Based Repayment Plans (IBR) are Income-dependent repayment plans
      available to Direct Loan borrowers with eligible loans (which do not
      include Uninsured private loans, Parent Plus loans, Perkins loans, FFEL
      program loans nor consolidation loans that repay those). You must have
      high debt relative to your income to be eligible for this repayment plan.
      The monthly minimum payments for the Income-Based Repayment (IBR)
      repayment plan are the lesser of 10%-15% (depending on origination date)
      of discretionary income and the standard 10-year paydown schedule. Your
      remaining balance may be subject to forgiveness after a certain number of
      years.
    </>
  ),
  icr: (
    <>
      Income-Contingent Repayment Plans (ICR) are Income-dependent repayment
      plans available to Direct Loan borrowers with eligible loans. The monthly
      minimum payments for the are the lesser of 20% of your discretionary
      income and a standard 12-year paydown schedule. Your remaining balance may
      be subject to forgiveness after a certain number of years. Parent PLUS
      Loans can not use this repayment plan unless they are consolidated under a
      Direct Consolidation Loan.
    </>
  )
}

const StudentLoanForm = ({
  data: {
    fields: {
      person_id,
      balance,
      is_perkins,
      is_ffel,
      is_parent_plus,
      is_subsidized,
      is_deferred,
      deferred_until_year,
      deferred_until_month,
      is_accruing,
      accrual_start_year,
      accrual_start_month,
      interest_rate,
      repayment_plan,
      monthly_payment,
      additional_payment,
      is_qualified,
      capitalized_amount,
      is_forgivable,
      forgiveness_year,
      forgiveness_month,
      is_pslf,
      is_pre_20140701,
      is_paying_additional
    },
    isModified,
    onSubmit,
    error
  },
  submitLabel = "Done",
  ownerOptions,
  isNew,
  onDelete
}) => {
  const currentYear = new Date().getFullYear()

  const months = [...Array(12).keys()].map(i =>
    new Date(0, i).toLocaleString("en-us", { month: "long" })
  )

  const yearOptions = [...Array(20).keys()].map(i => currentYear + i)

  let repayment_plans = REPAYMENT_PLAN_OPTIONS

  if (is_ffel.checked) {
    repayment_plans = repayment_plans.filter(
      option => option.value === "standard" || option.value === "ibr"
    )
  }

  if (is_parent_plus.checked) {
    repayment_plans = repayment_plans.filter(
      option => option.value === "standard" || option.value === "icr"
    )
  }

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column>
          <Worksheet error={error} style={{ borderTop: "1px solid #ccc" }}>
            <Worksheet.Body>
              <Worksheet.Row
                label="Who is the Student Loan holder/owner?"
                paddedField
              >
                <Worksheet.Field error={person_id.error}>
                  <Dropdown
                    {...person_id}
                    placeholder="Account Holder"
                    options={ownerOptions}
                    selection
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="What is the current balance of your loan?"
                description={
                  <>
                    Please give us your best estimate of your loan balance so
                    that we can better help you plan. If you do not know how
                    much you owe, search for your federal loans in the{" "}
                    <a
                      href="https://nslds.ed.gov/nslds/nslds_SA/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      National Student Loan Data System
                    </a>
                    .
                  </>
                }
                paddedField
              >
                <Worksheet.Field error={balance.error}>
                  <LabeledInput
                    {...balance}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="Is this a Perkins loan?"
                description={
                  <>
                    Perkins loans were available to undergraduate, graduate, and
                    professional students. A Perkins loan is a subsidized loan,
                    meaning that the federal government pays the loan’s interest
                    while you are in school, have a minimum payment of $30 or
                    $40 and unless extended by the school are paid in up to 120
                    monthly payments.
                  </>
                }
                paddedField
              >
                <Worksheet.Field error={is_perkins.error}>
                  <CheckboxYesNo {...is_perkins} fluid />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="Was  your loan obtained through the Federal Family Education Loan (FFEL) Program?"
                description={
                  <>
                    Family Family Education Loan Program loans (FFEL) are one
                    type of Federal loans, the other type being the Direct Loans
                  </>
                }
                paddedField
              >
                <Worksheet.Field error={is_ffel.error}>
                  <CheckboxYesNo {...is_ffel} fluid />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="Is your loan a Parent Plus loan or a Consolidation loan that includes a Parent PLUS?"
                description={
                  <>
                    The Parent Plus loan is a federal student loan (Direct or
                    FFEL) available to parents of dependent undergraduate
                    students.
                  </>
                }
                paddedField
              >
                <Worksheet.Field error={is_parent_plus.error}>
                  <CheckboxYesNo {...is_parent_plus} fluid />
                </Worksheet.Field>
              </Worksheet.Row>
              {!is_perkins.checked ? (
                <Worksheet.Row
                  label="Is this loan subsidized?"
                  description={
                    <>
                      Is the Loan Subsidized? Subsidized Loans include federal
                      student loans available to undergraduates with financial
                      need, such as Direct Subsidized Loans (Subsidized Stafford
                      Loans) and Federal Perkins Loans (no longer available).
                      Those loans may not be accruing interest during deferment
                      or post-graduation grace period. A consolidation loan can
                      have both a subsidized and an unsubsidized balance,
                      however Federal Perkins Loans included in a consolidation
                      loan, have lost their subsidized interest benefits. Please
                      separate consolidation loans with both a subsidized and
                      unsubsidized balance when entering them.
                    </>
                  }
                  paddedField
                >
                  <Worksheet.Field error={is_subsidized.error}>
                    <CheckboxYesNo {...is_subsidized} fluid />
                  </Worksheet.Field>
                </Worksheet.Row>
              ) : null}
              {is_subsidized.checked ? (
                <Worksheet.Row
                  label="Is this loan accruing interest?"
                  description={
                    <>
                      The U.S. Department of Education pays the interest on
                      Direct Subsidized Loans while you’re in school at least
                      half-time, for the first six months after you leave school
                      (referred to as a grace period), and during a period of
                      deferment. States and other organizations may have similar
                      programs for non-federal loans.
                    </>
                  }
                  paddedField
                >
                  <Worksheet.Field error={is_accruing.error}>
                    <CheckboxYesNo
                      {...is_accruing}
                      fluid
                      onChange={(e, { value }) => {
                        is_accruing.onChange(e, { value: value })
                        accrual_start_year.onChange(e, { value: "" })
                        accrual_start_month.onChange(e, { value: "" })
                      }}
                    />
                  </Worksheet.Field>
                  {is_accruing.value !== "indeterminate" &&
                  !is_accruing.checked ? (
                    <Worksheet.Row
                      label="When will interest start accruing?"
                      paddedField
                    >
                      <Worksheet.Field
                        error={
                          accrual_start_year.error || accrual_start_month.error
                        }
                      >
                        <Grid>
                          <Grid.Row>
                            <Grid.Column width={7}>
                              <Select
                                {...accrual_start_year}
                                fluid
                                placeholder="year"
                                options={yearOptions.map(value => ({
                                  text: value,
                                  value: String(value)
                                }))}
                              />
                            </Grid.Column>
                            <Grid.Column width={9}>
                              <Select
                                {...accrual_start_month}
                                fluid
                                placeholder="month"
                                options={months.map((month, index) => ({
                                  value: String(index + 1),
                                  text: month
                                }))}
                              />
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Worksheet.Field>
                    </Worksheet.Row>
                  ) : null}
                </Worksheet.Row>
              ) : null}
              <Worksheet.Row
                label="Is this loan in deferment?"
                description={
                  <>
                    If your loan payments have been postponed because you are
                    still in school or for another reason like hardship, your
                    loan is in deferment.
                  </>
                }
                paddedField
              >
                <Worksheet.Field error={is_deferred.error}>
                  <CheckboxYesNo
                    {...is_deferred}
                    fluid
                    onChange={(e, { value }) => {
                      is_deferred.onChange(e, { value: value })
                      deferred_until_year.onChange(e, { value: "" })
                      deferred_until_month.onChange(e, { value: "" })
                    }}
                  />
                </Worksheet.Field>
                {is_deferred.checked ? (
                  <Worksheet.Row
                    label="When will you need to start making payments again?"
                    description={
                      <>
                        Deferments typically end when your enrollment as a
                        qualified student or other hardship ends, or after a
                        fixed number of years.
                      </>
                    }
                    paddedField
                  >
                    <Worksheet.Field
                      error={
                        deferred_until_year.error || deferred_until_month.error
                      }
                    >
                      <Grid>
                        <Grid.Row>
                          <Grid.Column width={7}>
                            <Select
                              {...deferred_until_year}
                              fluid
                              placeholder="year"
                              options={yearOptions.map(value => ({
                                text: value,
                                value: String(value)
                              }))}
                            />
                          </Grid.Column>
                          <Grid.Column width={9}>
                            <Select
                              {...deferred_until_month}
                              fluid
                              placeholder="month"
                              options={months.map((month, index) => ({
                                value: String(index + 1),
                                text: month
                              }))}
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Worksheet.Field>
                  </Worksheet.Row>
                ) : null}
              </Worksheet.Row>
              <Worksheet.Row
                label="What is the interest rate?"
                description={
                  <>
                    Enter the annual interest rate charged after any subsidy
                    period has ended. Your interest rate will depend on the type
                    of loan you have and the year it was initiated (see{" "}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://studentaid.gov/understand-aid/types/loans/interest-rates#what-are-the-interest-rates-on-federal-student-loans-first-disbursed-before-july-1-2019"
                    >
                      studentaid.gov
                    </a>
                    ).
                  </>
                }
                paddedField
              >
                <Worksheet.Field error={interest_rate.error}>
                  <LabeledInput
                    {...interest_rate}
                    label=""
                    iconText="%"
                    placeholder="0"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              {!is_perkins.checked ? (
                <Worksheet.Row
                  label="What is your current repayment plan?"
                  description={
                    REPAYMENT_PLAN_DESCRIPTIONS[repayment_plan.value]
                  }
                  paddedField
                >
                  <Worksheet.Field error={repayment_plan.error}>
                    <Select
                      {...repayment_plan}
                      fluid
                      placeholder="Repayment Plan"
                      options={repayment_plans}
                    />
                  </Worksheet.Field>
                </Worksheet.Row>
              ) : null}
              {/* <Worksheet.Row
                label="Is this a qualified student loan?"
                description={
                  <>
                    A qualified student loan is a loan that has been used for
                    the sole purpose of paying qualified educational expenses.
                    Such expenses must be funded by the loan within 90 days
                    before or after a specific academic period.
                  </>
                }
                paddedField
              >
                <Worksheet.Field error={is_qualified.error}>
                  <CheckboxYesNo {...is_qualified} fluid />
                </Worksheet.Field>
              </Worksheet.Row> */}
              {repayment_plan.value === "ibr" ? (
                <Worksheet.Row
                  label="Does your loan qualify for IBR 2014?"
                  description={
                    <>
                      New Direct Loan borrowers with no outstanding balance on
                      Direct Loan Program or Federal Family Education Loan
                      (FFEL) before July 2014 qualify for lower monthly payment
                      (based on 10% of discretionary income). Borrowers with
                      consolidation loans originated after 2014 might also
                      qualify for this status. Please check with your provider.
                    </>
                  }
                  paddedField
                >
                  <Worksheet.Field error={is_pre_20140701.error}>
                    <CheckboxYesNo {...is_pre_20140701} reversed={true} fluid />
                  </Worksheet.Field>
                </Worksheet.Row>
              ) : null}
              {repayment_plan.value === "standard" || !is_deferred.checked ? (
                <Worksheet.Row
                  label={
                    is_deferred.checked ? (
                      <>
                        What will your minimum monthly payment be{" "}
                        <span style={{ textDecoration: "underline" }}>
                          when deferment ends
                        </span>
                        ?
                      </>
                    ) : (
                      <>
                        What is your{" "}
                        <span style={{ textDecoration: "underline" }}>
                          current
                        </span>{" "}
                        minimum monthly payment?
                      </>
                    )
                  }
                  description={
                    <>
                      Some loans have a minimum monthly payment. Please enter
                      the appropriate amount (for example, $50 for Direct
                      Stafford Loans, $40 for Perkins loans and $50 for PLUS
                      loans). The minimum monthly payment is $10 under PAYE,
                      REPAYE or IBR and $5 for ICR.
                    </>
                  }
                  paddedField
                >
                  <Worksheet.Field error={monthly_payment.error}>
                    <LabeledInput
                      {...monthly_payment}
                      label=""
                      iconText="$"
                      iconPosition="left"
                      placeholder="0"
                    />
                    {!!monthly_payment.value ? (
                      <span style={{ fontWeight: "normal" }}>
                        {formatCurrency(
                          12 * monthly_payment.value.replace(/,/g, "")
                        )}{" "}
                        per year
                      </span>
                    ) : null}
                  </Worksheet.Field>
                </Worksheet.Row>
              ) : null}
              {repayment_plan.value === "standard" && !is_deferred.checked ? (
                <Worksheet.Row
                  label="Are you making additional monthly payments above the minimum payment?"
                  paddedField
                >
                  <Worksheet.Field error={is_paying_additional.error}>
                    <CheckboxYesNo
                      {...is_paying_additional}
                      fluid
                      onChange={(e, { value }) => {
                        is_paying_additional.onChange(e, { value: value })
                        additional_payment.onChange(e, { value: "" })
                      }}
                    />
                  </Worksheet.Field>
                  {is_paying_additional.value !== "indeterminate" &&
                  is_paying_additional.checked ? (
                    <Worksheet.Row label="How much?" paddedField>
                      <Worksheet.Field error={person_id.error}>
                        <LabeledInput
                          {...additional_payment}
                          label=""
                          iconText="$"
                          iconPosition="left"
                          placeholder="0"
                        />
                      </Worksheet.Field>
                    </Worksheet.Row>
                  ) : null}
                </Worksheet.Row>
              ) : null}
              {repayment_plan.value && repayment_plan.value !== "standard" ? (
                <Worksheet.Row
                  label="Are you pursuing loan forgiveness?"
                  description={
                    <>
                      Income-dependent repayment loans may be eligible for loan
                      forgiveness after a certain number of years (typically 20)
                      if your income has remained low enough to maintain a
                      balance. If you qualify under the public service loan
                      forgiveness program, the remaining debt is forgiven after
                      10 years and the forgiveness is tax-free. We do not
                      provide advice on forgiveness eligibility or likelihood,
                      but if you would like to plan to pursue forgiveness, you
                      can enter the expected forgiveness year.
                    </>
                  }
                  paddedField
                >
                  <Worksheet.Field error={is_forgivable.error}>
                    <CheckboxYesNo
                      {...is_forgivable}
                      fluid
                      onChange={(e, { value }) => {
                        is_forgivable.onChange(e, { value: value })
                        forgiveness_year.onChange(e, { value: "" })
                        forgiveness_month.onChange(e, { value: "" })
                      }}
                    />
                  </Worksheet.Field>
                  {is_forgivable.checked ? (
                    <Worksheet.Row
                      label="When do you expect any remaining balance to be forgiven?"
                      description={<></>}
                      paddedField
                    >
                      <Worksheet.Field
                        error={
                          forgiveness_year.error || forgiveness_month.error
                        }
                      >
                        <Grid>
                          <Grid.Row>
                            <Grid.Column width={7}>
                              <Select
                                {...forgiveness_year}
                                fluid
                                placeholder="year"
                                options={yearOptions.map(value => ({
                                  text: value,
                                  value: String(value)
                                }))}
                              />
                            </Grid.Column>
                            <Grid.Column width={9}>
                              <Select
                                {...forgiveness_month}
                                fluid
                                placeholder="month"
                                options={months.map((month, index) => ({
                                  value: String(index + 1),
                                  text: month
                                }))}
                              />
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Worksheet.Field>
                    </Worksheet.Row>
                  ) : null}
                  {is_forgivable.checked ? (
                    <Worksheet.Row
                      label="Does this loan qualify for PSLF?"
                      description={
                        <>
                          If you are employed by a U.S. federal, state, local,
                          or tribal government or not-for-profit organization,
                          you might be eligible for the Public Service Loan
                          Forgiveness Program (PSLF). Amounts forgiven under the
                          PSLF Program are not considered income by the Internal
                          Revenue Service. Therefore, you will not have to pay
                          federal income tax on the amount of your Direct Loans
                          that is forgiven. Check this box if eligible.
                        </>
                      }
                      paddedField
                    >
                      <Worksheet.Field error={is_pslf.error}>
                        <CheckboxYesNo {...is_pslf} fluid />
                      </Worksheet.Field>
                    </Worksheet.Row>
                  ) : null}
                </Worksheet.Row>
              ) : null}
              {/* {is_qualified.checked ? ( */}
              <Worksheet.Row
                label="How much unpaid interest has this loan accrued?"
                description={
                  <>
                    Unpaid interest is deferred or{" "}
                    <a href="https://www.salliemae.com/student-loans/manage-your-private-student-loan/understand-student-loan-payments/learn-about-interest-and-capitalization/">
                      "capitalized"
                    </a>{" "}
                    interest that has been added to your student loan balance
                    during deferment, increasing the total amount you'll have to
                    pay back. <strong>Enter $0 if you don't know.</strong>
                  </>
                }
                paddedField
              >
                <Worksheet.Field error={capitalized_amount.error}>
                  <LabeledInput
                    {...capitalized_amount}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              {/* ) : null} */}
            </Worksheet.Body>
            <Footer
              isNew={isNew}
              submitLabel={submitLabel}
              onSubmit={onSubmit}
              onDelete={onDelete}
              isModified={isModified}
              deleteLabel="Delete"
              error={error}
            />
          </Worksheet>{" "}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

const StudentLoanFormContainer = FormContainer(
  {
    person_id: {
      type: "string"
    },
    balance: { type: "unit", required: true, negative: true },
    is_perkins: { type: "boolean", defaultValue: false },
    is_ffel: { type: "boolean", defaultValue: false },
    is_parent_plus: { type: "boolean", defaultValue: false },
    is_subsidized: { type: "boolean", defaultValue: true },
    is_deferred: { type: "boolean", defaultValue: false },
    deferred_until_year: { type: "string", required: false },
    deferred_until_month: { type: "string", required: false },
    is_accruing: { type: "boolean", defaultValue: true },
    accrual_start_year: { type: "string", required: false },
    accrual_start_month: { type: "string", required: false },
    interest_rate: { type: "decimal", minValue: 0, maxValue: 100 },
    repayment_plan: { type: "string", defaultValue: "standard" },
    monthly_payment: { type: "unit", required: false },
    additional_payment: { type: "unit", required: false },
    is_qualified: {
      type: "boolean",
      defaultValue: true,
      canBeIndeterminate: false
    },
    capitalized_amount: { type: "unit", defaultValue: 0, required: false },
    is_forgivable: { type: "boolean", defaultValue: false },
    forgiveness_year: { type: "string", required: false },
    forgiveness_month: { type: "string", required: false },
    is_pslf: { type: "boolean", defaultValue: false },
    is_pre_20140701: { type: "boolean" },
    is_paying_additional: {
      type: "boolean",
      required: false,
      submit: false
    }
  },
  ({ fields }, ...form) => {
    const {
      is_accruing,
      accrual_start_year,
      accrual_start_month,
      is_deferred,
      deferred_until_year,
      deferred_until_month,
      repayment_plan,
      is_forgivable,
      forgiveness_year,
      forgiveness_month,
      monthly_payment
      // is_qualified,
      // capitalized_amount
    } = fields
    if (!is_accruing.checked) {
      if (accrual_start_year.value === "") {
        return {
          fields: {
            accrual_start_year: `Year is required`
          }
        }
      }
      if (accrual_start_month.value === "") {
        return {
          fields: {
            accrual_start_month: `Month is required`
          }
        }
      }
    }

    if (is_deferred.checked) {
      if (deferred_until_year.value === "") {
        return {
          fields: {
            deferred_until_year: `Year is required`
          }
        }
      }
      if (deferred_until_month.value === "") {
        return {
          fields: {
            deferred_until_month: `Month is required`
          }
        }
      }
    }

    if (repayment_plan.value !== "standard") {
      if (is_forgivable.value === "indeterminate") {
        return {
          fields: {
            is_forgivable: "This field is required"
          }
        }
      }

      if (is_forgivable.checked) {
        if (forgiveness_year.value === "") {
          return {
            fields: {
              forgiveness_year: `Year is required`
            }
          }
        }
        if (forgiveness_month.value === "") {
          return {
            fields: {
              forgiveness_month: `Month is required`
            }
          }
        }
      }
    }

    if (repayment_plan === "standard") {
      if (!monthly_payment.value) {
        return {
          fields: {
            monthly_payment: "This field is required"
          }
        }
      }
    } else if (!is_deferred.checked) {
      if (!monthly_payment.value) {
        return {
          fields: {
            monthly_payment: "This field is required"
          }
        }
      }
    }

    // if (is_qualified.checked && !capitalized_amount.value) {
    //   return {
    //     fields: {
    //       capitalized_amount: "This field is required"
    //     }
    //   }
    // }
  }
)(StudentLoanForm)

const StudentLoan = ({ household, account, type, ...formProps }) => {
  const isNew = account === null
  const [peopleObject] = useStore("householdPeople", household.id)
  const people = Object.values(peopleObject)
  const onlyClientId = people.find(({ role }) => role === "client").id
  const values = isNew ? { person_id: onlyClientId } : { ...account }
  let ownerOptions = people
    .filter(({ role }) => role === "spouse" || role === "client")
    .map(({ id: value, first_name: text }) => ({
      value,
      text
    }))
  if (!isNew) {
    const { additional_payment } = account
    const is_paying_additional = !!additional_payment
    Object.assign(values, { is_paying_additional })
  }

  const { submit } = formProps

  const onSubmit = values => {
    const {
      additional_payment,
      repayment_plan,
      is_perkins,
      is_deferred,
      is_subsidized,
      is_qualified,
      is_accruing,
      is_forgivable
    } = values

    if (repayment_plan !== "standard" && is_deferred) {
      values["monthly_payment"] = null
    }
    if (additional_payment && (repayment_plan !== "standard" || is_deferred)) {
      values["additional_payment"] = null
    }
    if (!is_deferred) {
      values["deferred_until_year"] = null
      values["deferred_until_month"] = null
    }
    if (is_perkins) {
      values["is_subsidized"] = true
    }
    if (!is_subsidized) {
      values["is_accruing"] = true
    }
    if (is_accruing || !is_subsidized) {
      values["accrual_start_year"] = null
      values["accrual_start_month"] = null
    }
    if (!is_forgivable) {
      values["forgiveness_year"] = null
      values["forgiveness_month"] = null
    }
    if (!is_qualified) {
      values["capitalized_amount"] = null
    }

    return submit(values)
  }

  return (
    <StudentLoanFormContainer
      {...formProps}
      values={values}
      ownerOptions={ownerOptions}
      submit={onSubmit}
    />
  )
}

export default StudentLoan
